/**
 * Student Dashboard - Home Page
 * 
 * Single-glance summary showing:
 * - Overall attendance percentage
 * - Recent published marks
 * - Current fee balance
 */

import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  GraduationCap, 
  CheckCircle, 
  IndianRupee, 
  TrendingUp,
  AlertCircle
} from 'lucide-react';

export function StudentDashboard() {
  const { user, logout } = useAuth();
  
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const dashboardRes = await apiService.getStudentDashboard();
      console.log('[Student Dashboard] Loaded:', dashboardRes.data);
      
      setDashboardData(dashboardRes.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const stats = dashboardData?.stats || {
    overallAttendance: 0,
    subjectsWithMarks: 0,
    feePending: 0
  };

  const attendance = dashboardData?.attendance || {
    present: 0,
    absent: 0,
    total: 0,
    percentage: 0
  };

  const classroom = dashboardData?.classroom || null;

  const attendanceColor = attendance.percentage >= 75 ? '#22c55e' : attendance.percentage >= 60 ? '#eab308' : '#ef4444';

  if (!user) return null;

  return (
    <Layout
      schoolName={'SMS'}
      principalName={user.loginId || 'Student'}
      onLogout={logout}
      role="student"
    >
      <div style={{ padding: '32px' }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
            Welcome back, Student!
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            {classroom ? `Class: ${classroom.name} ${classroom.section}` : 'Dashboard'}
          </p>
        </div>

        {error && (
          <Card>
            <div style={{ padding: '24px', textAlign: 'center', color: '#ef4444' }}>
              <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
              <p>{error}</p>
              <button
                onClick={loadDashboardData}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          </Card>
        )}

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          {/* Attendance Card */}
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '8px' }}>
                  <CheckCircle size={24} style={{ color: '#2563eb' }} />
                </div>
                <TrendingUp size={20} style={{ color: attendanceColor }} />
              </div>
              {isLoading ? (
                <Skeleton height="60px" />
              ) : (
                <>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: attendanceColor, marginBottom: '4px' }}>
                    {Math.round(attendance.percentage)}%
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Overall Attendance
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                    {attendance.present} present out of {attendance.total} sessions
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Marks Card */}
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '12px', background: '#dcfce7', borderRadius: '8px' }}>
                  <GraduationCap size={24} style={{ color: '#16a34a' }} />
                </div>
              </div>
              {isLoading ? (
                <Skeleton height="60px" />
              ) : (
                <>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#16a34a', marginBottom: '4px' }}>
                    {stats.subjectsWithMarks}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Subjects with Marks
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                    Published assessments
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Fee Balance Card */}
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '12px', background: stats.feePending > 0 ? '#fee2e2' : '#dcfce7', borderRadius: '8px' }}>
                  <IndianRupee size={24} style={{ color: stats.feePending > 0 ? '#dc2626' : '#16a34a' }} />
                </div>
              </div>
              {isLoading ? (
                <Skeleton height="60px" />
              ) : (
                <>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: stats.feePending > 0 ? '#dc2626' : '#16a34a', marginBottom: '4px' }}>
                    ₹{Math.abs(stats.feePending / 100).toFixed(0)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Fee Balance
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                    {stats.feePending > 0 ? 'Pending' : stats.feePending < 0 ? 'Advance' : 'Paid'}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
